import { useTranslation } from "react-i18next"

import type { HostedAgentEvalRun } from "@/api/admin-api"

import { Central, ShieldQuestion, TriangleAlert } from "@/components/icons"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"

import { HostedAgentEvalCaseDetailCodeText } from "./hosted-agent-eval-outcome-badge"
import {
  formatHostedAgentEvalPercent,
  HOSTED_AGENT_EVAL_UNMEASURED_HATCH,
  hostedAgentEvalBarSegments,
  hostedAgentEvalComparability,
  shortHostedAgentEvalDigest,
  summarizeHostedAgentEvalTotals,
} from "../utils/hosted-agent-evals"
import { formatHostedAgentTimestamp } from "../utils/hosted-agents"

// Suite-level summary (#3028). The whole point of this surface: never let an
// admin read "5/7" and conclude the revision is fine when two of the seven
// cases produced no evidence at all.
//
// Three things are always on screen together:
//   1. a three-segment bar where the unmeasured share is HATCHED, not coloured
//   2. the counts spelled out in words, all three of them
//   3. evidence coverage — (passed + failed) / cases — as its own number

export function HostedAgentEvalRunSummary({ run }: { run: HostedAgentEvalRun }) {
  const { t, i18n } = useTranslation()
  // The status matters to the arithmetic: a crashed suite keeps its last
  // progress snapshot, so the cases it never reached exist only as the gap
  // between `cases` and the reported buckets.
  const view = summarizeHostedAgentEvalTotals(run.totals, run.status)
  const segments = hostedAgentEvalBarSegments(view)
  const comparability = hostedAgentEvalComparability(run)
  const started = formatHostedAgentTimestamp(run.started_at, i18n.language)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">
          {t("adminDashboard.hostedAgents.evals.summary.title", { defaultValue: "Suite result" })}
        </span>
        {started ? <span className="text-muted-foreground text-xs">{started}</span> : null}
        <HostedAgentEvalRunFlags run={run} />
      </div>

      {/* Axis 1 made visible: the hatched slice is the part of the suite that
          says nothing either way. It is never green and never red. */}
      <div
        className="bg-muted flex h-2 w-full overflow-hidden rounded-md"
        role="img"
        aria-label={t("adminDashboard.hostedAgents.evals.summary.barLabel", {
          defaultValue: "{{passed}} passed, {{failed}} failed, {{unmeasured}} not measured",
          passed: view.passed,
          failed: view.failed,
          unmeasured: view.unmeasured,
        })}
      >
        <div className="bg-success h-full" style={{ width: `${segments.passed}%` }} />
        <div className="bg-destructive h-full" style={{ width: `${segments.failed}%` }} />
        <div
          className="h-full"
          style={{
            width: `${segments.unmeasured}%`,
            backgroundImage: HOSTED_AGENT_EVAL_UNMEASURED_HATCH,
          }}
        />
      </div>

      {/* Axis 2 spelled out. Never a bare "5/7". While the suite runs the gap
          is "not yet"; once it settles, the same gap is the finding. */}
      <p aria-live="polite" className="text-sm" role="status">
        {view.isRunning
          ? t("adminDashboard.hostedAgents.evals.summary.countsRunning", {
              defaultValue:
                "{{passed}} passed · {{failed}} failed · {{pending}} with no result yet (of {{cases}})",
              passed: view.passed,
              failed: view.failed,
              pending: view.unmeasured,
              cases: view.cases,
            })
          : t("adminDashboard.hostedAgents.evals.summary.counts", {
              defaultValue:
                "{{passed}} passed · {{failed}} failed · {{unmeasured}} could not be measured (of {{cases}})",
              passed: view.passed,
              failed: view.failed,
              unmeasured: view.unmeasured,
              cases: view.cases,
            })}
      </p>

      {/* Cases that never reported at all are a different kind of hole from
          cases that reported "could not measure" — a suite that died after
          case 3 of 7 must not read as though four cases answered. */}
      {!view.isRunning && view.unreported > 0 ? (
        <p className="border-foreground bg-warning text-warning-foreground rounded-md border px-2 py-1 text-xs">
          {t("adminDashboard.hostedAgents.evals.summary.unreported", {
            defaultValue:
              "{{unreported}} of {{cases}} cases never reported a result — the suite stopped before they were measured.",
            unreported: view.unreported,
            cases: view.cases,
          })}
        </p>
      ) : null}

      <div className="grid gap-3 rounded-lg border p-3 sm:grid-cols-2">
        <div>
          <div className="text-muted-foreground text-xs">
            {t("adminDashboard.hostedAgents.evals.summary.coverage", {
              defaultValue: "Evidence coverage",
            })}
          </div>
          <div
            className={`text-lg font-semibold ${view.hasEvidenceGap ? "border-foreground bg-warning text-warning-foreground rounded-md border px-2 py-1" : ""}`}
          >
            {view.passed + view.failed}/{view.cases}
            <span className="ml-2 text-sm font-normal">
              {formatHostedAgentEvalPercent(view.coverage, i18n.language)}
            </span>
          </div>
        </div>
        <div>
          <div className="text-muted-foreground text-xs">
            {t("adminDashboard.hostedAgents.evals.summary.score", { defaultValue: "Score" })}
          </div>
          <div className="text-lg font-semibold">
            {formatHostedAgentEvalPercent(view.score, i18n.language)}
            {view.hasEvidenceGap ? <span className="text-foreground">*</span> : null}
          </div>
        </div>
      </div>

      {/* The score is honest arithmetic (passed / cases) — it is the READING
          that needs protecting, so the footnote is not optional. */}
      {view.hasEvidenceGap ? (
        <p className="border-foreground bg-warning text-warning-foreground rounded-md border px-2 py-1 text-xs">
          {view.isRunning
            ? t("adminDashboard.hostedAgents.evals.summary.scoreNoteRunning", {
                defaultValue:
                  "* The suite is still running: {{pending}} of {{cases}} cases have no result yet, so this score is provisional.",
                pending: view.unmeasured,
                cases: view.cases,
              })
            : t("adminDashboard.hostedAgents.evals.summary.scoreNote", {
                defaultValue:
                  "* {{unmeasured}} of {{cases}} cases were not measured — this score is not a clean bill of health.",
                unmeasured: view.unmeasured,
                cases: view.cases,
              })}
        </p>
      ) : null}

      {/* Nothing failed, yet the suite proved nothing about part of the agent.
          This must never be dressed up as a pass. */}
      {view.isIncomplete ? (
        <Alert>
          <TriangleAlert className="text-foreground size-4" />
          <AlertTitle>
            {t("adminDashboard.hostedAgents.evals.summary.incompleteTitle", {
              defaultValue: "Suite incomplete",
            })}
          </AlertTitle>
          <AlertDescription>
            {t("adminDashboard.hostedAgents.evals.summary.incompleteBody", {
              defaultValue:
                "Nothing failed, but {{unmeasured}} of {{cases}} cases could not be measured. This run does not show that the agent works — only that it was not caught failing.",
              unmeasured: view.unmeasured,
              cases: view.cases,
            })}
          </AlertDescription>
        </Alert>
      ) : null}

      {run.status === "failed" ? (
        <Alert>
          <TriangleAlert className="text-foreground size-4" />
          <AlertTitle>
            {t("adminDashboard.hostedAgents.evals.summary.suiteFailedTitle", {
              defaultValue: "The suite itself failed",
            })}
          </AlertTitle>
          <AlertDescription>
            {t("adminDashboard.hostedAgents.evals.summary.suiteFailedBody", {
              defaultValue:
                "The eval run crashed before it finished. This says nothing about the agent — the cases below are whatever had already been measured.",
            })}
          </AlertDescription>
        </Alert>
      ) : null}

      {run.aborted ? (
        <Alert>
          <TriangleAlert className="text-foreground size-4" />
          <AlertTitle>
            {t("adminDashboard.hostedAgents.evals.summary.abortedTitle", {
              defaultValue: "The suite stopped early",
            })}
          </AlertTitle>
          <AlertDescription>
            <HostedAgentEvalCaseDetailCodeText code={run.aborted} />
          </AlertDescription>
        </Alert>
      ) : null}

      {comparability.notComparable ? (
        <Alert>
          <Central.ChartCompare className="text-foreground size-4" />
          <AlertTitle>
            {t("adminDashboard.hostedAgents.evals.summary.notComparableTitle", {
              defaultValue: "Measured across two revisions",
            })}
          </AlertTitle>
          <AlertDescription>
            {t("adminDashboard.hostedAgents.evals.summary.notComparableBody", {
              defaultValue:
                'The agent changed while the suite was running (v{{start}} → v{{finish}}). This run cannot support a "the new revision is better" claim — rerun it against a revision that stays still.',
              start: comparability.revisionAtStart ?? "?",
              finish: comparability.revisionAtFinish ?? "?",
            })}
          </AlertDescription>
        </Alert>
      ) : null}

      {comparability.unverified ? (
        <Alert>
          <ShieldQuestion className="text-foreground size-4" />
          <AlertTitle>
            {t("adminDashboard.hostedAgents.evals.summary.unverifiedTitle", {
              defaultValue: "Provenance unverified",
            })}
          </AlertTitle>
          <AlertDescription>
            {t("adminDashboard.hostedAgents.evals.summary.unverifiedBody", {
              defaultValue:
                'The agent definition could not be re-read after the run, so we cannot confirm what was measured. That is a "cannot say", not a clean result.',
            })}
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  )
}

/** The badges that qualify a run — subset, comparability, verification. */
export function HostedAgentEvalRunFlags({ run }: { run: HostedAgentEvalRun }) {
  const { t } = useTranslation()
  const comparability = hostedAgentEvalComparability(run)
  const caseCount = run.provenance.eval_case_ids.length

  return (
    <>
      {comparability.partial ? (
        <Badge variant="outline">
          {t("adminDashboard.hostedAgents.evals.flags.partial", {
            defaultValue: "Subset ({{n}} cases)",
            n: caseCount,
          })}
        </Badge>
      ) : null}
      {comparability.notComparable ? (
        <Badge variant="warning">
          {t("adminDashboard.hostedAgents.evals.flags.notComparable", {
            defaultValue: "Not comparable",
          })}
        </Badge>
      ) : null}
      {comparability.unverified ? (
        <Badge variant="outline">
          {t("adminDashboard.hostedAgents.evals.flags.unverified", {
            defaultValue: "Provenance unverified",
          })}
        </Badge>
      ) : null}
    </>
  )
}

/**
 * What was under test. Without this stamp a score is an anecdote: the agent
 * revision, the model actually used, the skill versions and the tool list are
 * what make one run comparable with another.
 */
export function HostedAgentEvalProvenanceCard({ run }: { run: HostedAgentEvalRun }) {
  const { t } = useTranslation()
  const provenance = run.provenance

  const skills = Object.entries(provenance.skill_digests)
  const model =
    provenance.model.model_id ||
    t("adminDashboard.hostedAgents.evals.provenance.modelAuto", { defaultValue: "Auto" })

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <p className="text-sm font-medium">
        {t("adminDashboard.hostedAgents.evals.provenance.title", {
          defaultValue: "What was measured",
        })}
      </p>
      <dl className="grid gap-2 text-xs sm:grid-cols-2">
        <ProvenanceRow
          label={t("adminDashboard.hostedAgents.evals.provenance.revision", {
            defaultValue: "Agent revision",
          })}
          value={`v${provenance.agent_revision}${
            provenance.agent_revision_at_finish != null &&
            provenance.agent_revision_at_finish !== provenance.agent_revision
              ? ` → v${provenance.agent_revision_at_finish}`
              : ""
          }`}
        />
        <ProvenanceRow
          value={provenance.model.selection ? `${model} (${provenance.model.selection})` : model}
          label={t("adminDashboard.hostedAgents.evals.provenance.model", {
            defaultValue: "Model",
          })}
        />
        <ProvenanceRow
          label={t("adminDashboard.hostedAgents.evals.provenance.tools", {
            defaultValue: "Tools",
          })}
          value={
            provenance.tools.length
              ? provenance.tools.join(", ")
              : t("adminDashboard.hostedAgents.evals.provenance.none", { defaultValue: "None" })
          }
        />
        <ProvenanceRow
          label={t("adminDashboard.hostedAgents.evals.provenance.libraries", {
            defaultValue: "Libraries",
          })}
          value={
            provenance.libraries.length
              ? provenance.libraries.join(", ")
              : t("adminDashboard.hostedAgents.evals.provenance.none", { defaultValue: "None" })
          }
        />
        <ProvenanceRow
          mono
          value={shortHostedAgentEvalDigest(provenance.instructions_sha256)}
          label={t("adminDashboard.hostedAgents.evals.provenance.instructions", {
            defaultValue: "Instructions digest",
          })}
        />
        <ProvenanceRow
          mono
          value={shortHostedAgentEvalDigest(provenance.eval_cases_sha256)}
          label={t("adminDashboard.hostedAgents.evals.provenance.exam", {
            defaultValue: "Test digest",
          })}
        />
        {provenance.hermes_agent_version ? (
          <ProvenanceRow
            mono
            value={provenance.hermes_agent_version}
            label={t("adminDashboard.hostedAgents.evals.provenance.hermesVersion", {
              defaultValue: "Hermes agent version",
            })}
          />
        ) : null}
      </dl>

      <div className="space-y-1">
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.hostedAgents.evals.provenance.skills", {
            defaultValue: "Skill versions",
          })}
        </p>
        {skills.length === 0 ? (
          <p className="text-muted-foreground text-xs">
            {t("adminDashboard.hostedAgents.evals.provenance.none", { defaultValue: "None" })}
          </p>
        ) : (
          <ul className="space-y-0.5">
            {skills.map(([name, digest]) => (
              <li key={name} className="flex flex-wrap items-center gap-2 text-xs">
                <span>{name}</span>
                {/* A skill that could not be resolved digests as "unavailable" —
                    that absence is itself provenance and is shown, not hidden. */}
                {digest === "unavailable" ? (
                  <Badge variant="warning">
                    {t("adminDashboard.hostedAgents.evals.provenance.skillUnavailable", {
                      defaultValue: "unavailable",
                    })}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground font-mono">
                    {shortHostedAgentEvalDigest(digest)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function ProvenanceRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`break-words ${mono ? "font-mono" : ""}`}>{value}</dd>
    </div>
  )
}
